/**
 * Dataset Assistant component.
 *
 * In compact mode (sidebar), renders a 52px toolbar with a dropdown
 * ConversationSelector, new-analysis button, and custom header actions.
 * In fullscreen mode (!compactMode), renders a full left-hand
 * ConversationSidebar and a minimal header with only the header actions.
 */
import React, { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { HttpAgent } from "@ag-ui/client";
import { AssistantRuntimeProvider, type ThreadMessageLike } from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { AssistantUnavailable } from "datasets/components/assistant/AssistantUnavailable";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { Loader2, Plus } from "lucide-react";
import { ShareIcon } from "~/assets/icons";

import { AnalysisAgentTools } from "~/components/assistant-v2/AnalysisAgentTools";
import { Thread } from "~/components/assistant-v2/Thread";
import {
  getActiveConversationId,
  setActiveConversationId,
  useAgUiPersistence,
  useRunningConversations,
} from "~/components/assistant-v2/useAgUiPersistence";
import { type AgUiState, useAgUiState } from "~/components/assistant-v2/useAgUiState";
import { useAssistantFeatures } from "~/components/assistant-v2/useAssistantFeatures";
import { useContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useConversationName } from "~/components/assistant-v2/useConversationName";
import { useStreamingTurnIds } from "~/components/assistant-v2/useMessageFeedback";
import { useRunError } from "~/components/assistant-v2/useRunError";
import { useSkillSync } from "~/components/assistant-v2/useSkillSync";

import { AssistantProvider } from "~/components/assistant/AssistantContext";
import { ConversationSelector } from "~/components/assistant/ConversationSelector";
import { IconButton } from "~/components/assistant/PopoverButton";
import { ShareConversationModal } from "~/components/assistant/ShareConversationModal";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { filterVisibleServers } from "~/components/assistant/hooks/useMcpServers";
import type {
  MCPServer,
  PartialSkill,
  Skill,
  SkillCreate,
  SkillUpdate,
} from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";
import { ConversationSidebar } from "./ConversationSidebar";
import { useConversationManager } from "./useConversationManager";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

// ---------------------------------------------------------------------------
// ConversationRuntime — one instance per visited conversation. Each is keyed
// on conversationId so it mounts/unmounts independently. Multiple instances
// can be alive simultaneously (hidden via CSS), allowing concurrent SSE
// streams to stay open across conversation switches.
// ---------------------------------------------------------------------------

const MAX_TABS = 5;

interface ConversationRuntimeProps {
  conversationId: string;
  datasetId: string;
  datasetTitle: string;
  api: AssistantApi;
  suggestions?: Array<{ title: string; prompt: string }>;
  updateConversationName: (conversationId: string, displayName: string) => void;
  refreshSkills: () => Promise<void>;
  skillsEnabled: boolean;
  systemSkills: PartialSkill[];
  userSkills: PartialSkill[];
  datasetSkills: PartialSkill[];
  isLoadingSkills: boolean;
  createSkill: (skill: SkillCreate) => Promise<void>;
  updateSkill: (skillId: string, skill: SkillUpdate) => Promise<void>;
  deleteSkill: (skillId: string) => Promise<void>;
  getSkill: (skillId: string) => Promise<Skill>;
  mcpServers: MCPServer[];
  selectedMcpServers: string[];
  toggleMcpServer: (serverId: string) => void;
}

/**
 * Waits for /connect state, then renders the AG-UI runtime for one conversation.
 */
const ConversationRuntime: FC<ConversationRuntimeProps> = (props) => {
  const { initialState, initialMessages } = useAgUiState(
    VITE_ANALYSIS_AGENT_API,
    props.conversationId
  );

  if (!initialState) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }
  return (
    <ConversationRuntimeInner
      {...props}
      initialState={initialState}
      initialMessages={initialMessages}
    />
  );
};

const ConversationRuntimeInner: FC<
  ConversationRuntimeProps & {
    initialState: AgUiState;
    initialMessages: ThreadMessageLike[] | null;
  }
> = ({
  conversationId,
  datasetId,
  datasetTitle,
  api,
  suggestions,
  updateConversationName,
  refreshSkills,
  skillsEnabled,
  systemSkills,
  userSkills,
  datasetSkills,
  isLoadingSkills,
  createSkill,
  updateSkill,
  deleteSkill,
  getSkill,
  mcpServers,
  selectedMcpServers,
  toggleMcpServer,
  initialState,
  initialMessages,
}) => {
  const agent = useMemo(
    () =>
      new HttpAgent({
        url: `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/messages_agui`,
        headers: { Accept: "text/event-stream" },
        initialState,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once per mount
    []
  );

  const runtime = useAgUiRuntime({
    agent,
    showThinking: true,
    logger: {
      debug: (...args: any[]) => console.debug("[AG-UI]", ...args),
      error: (...args: any[]) => console.error("[AG-UI]", ...args),
    },
  });

  const contextBuffer = useContextBuffer(agent);
  const { error: runError, clearError } = useRunError(agent);
  const streamingTurnIds = useStreamingTurnIds(agent, runtime);

  useConversationName(agent, updateConversationName);
  useSkillSync(agent, refreshSkills);
  const persistence = useAgUiPersistence({
    agent,
    runtime,
    api,
    conversationId,
    clearError,
    initialMessages,
  });

  return (
    <AssistantProvider api={api} conversationId={conversationId} contextId={datasetId}>
      <AssistantRuntimeProvider runtime={runtime}>
        <AnalysisAgentTools />
        <Thread
          conversationId={conversationId}
          contextId={datasetId}
          suggestions={suggestions}
          contextBuffer={contextBuffer}
          isLoadingHistory={persistence.isLoadingHistory}
          historyHasMessages={persistence.historyHasMessages}
          runError={runError}
          streamingTurnIds={streamingTurnIds}
          isRunningRemotely={persistence.isRunningRemotely}
          onCancelRun={persistence.onCancelRun}
          isCancelling={persistence.isCancelling}
          skillsEnabled={skillsEnabled}
          systemSkills={systemSkills}
          userSkills={userSkills}
          datasetSkills={datasetSkills}
          isLoadingSkills={isLoadingSkills}
          onCreateSkill={createSkill}
          onUpdateSkill={updateSkill}
          onDeleteSkill={deleteSkill}
          onGetSkill={getSkill}
          mcpServers={mcpServers}
          selectedMcpServers={selectedMcpServers}
          onToggleMcpServer={toggleMcpServer}
          showToolSelector={true}
          placeholder={`Ask anything about ${datasetTitle}...`}
        />
      </AssistantRuntimeProvider>
    </AssistantProvider>
  );
};

// ---------------------------------------------------------------------------
// DatasetAssistant — outer shell managing conversations, sidebar, header
// ---------------------------------------------------------------------------

export interface DatasetAssistantProps {
  datasetId: string;
  className?: string;
  headerActions?: ReactNode;
  compactMode?: boolean;
}

// @lat: [[frontend/ai-assistant#Dataset Assistant]]
export function DatasetAssistant({
  datasetId,
  className,
  headerActions,
  compactMode = false,
}: DatasetAssistantProps) {
  const { data: dataset } = useDataset(datasetId);
  const datasetTitle = dataset?.title ?? datasetId;
  const initialConversationId = getActiveConversationId(datasetId);

  const suggestions = useMemo(() => {
    const prompts = dataset?.samplePrompts
      ?.filter((p) => p.trim().length > 0)
      .map((p) => ({ title: p, prompt: p }));
    return prompts && prompts.length > 0 ? prompts : undefined;
  }, [dataset?.samplePrompts]);

  const conversationShortcuts = dataset?.conversationShortcuts ?? [];

  const {
    conversations,
    currentConversationId,
    isLoading: isLoadingConversations,
    isCreating,
    loadError,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    updateConversationName,
  } = useConversationManager({
    api,
    datasetId,
    initialConversationId,
  });

  useEffect(() => {
    setActiveConversationId(datasetId, currentConversationId);
  }, [datasetId, currentConversationId]);

  const {
    skillsEnabled,
    mcpServers,
    selectedMcpServers,
    toggleMcpServer,
    systemSkills,
    userSkills,
    datasetSkills,
    isLoadingSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    refreshSkills,
  } = useAssistantFeatures({ api, contextId: datasetId, enableSkills: true });

  const visibleServers = useMemo(
    () => filterVisibleServers(mcpServers, dataset?.externalMcpAccess ?? []),
    [mcpServers, dataset?.externalMcpAccess]
  );

  const runningIds = useRunningConversations();
  const isConversationStreaming = useCallback((id: string) => runningIds.has(id), [runningIds]);

  useEffect(() => {
    trackEvent("assistant_ui_opened", {
      dataset_id: datasetId,
      context: "dataset",
    });
    return () => {
      trackEvent("assistant_ui_closed", {
        dataset_id: datasetId,
        context: "dataset",
      });
    };
  }, [datasetId]);

  const handleNewChat = useCallback(async () => {
    trackEvent("assistant_new_chat_clicked", {
      dataset_id: datasetId,
      context: "dataset",
    });
    await createConversation();
  }, [datasetId, createConversation]);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      if (conversationId === currentConversationId) {
        return;
      }
      trackEvent("assistant_conversation_selected", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      selectConversation(conversationId);
    },
    [datasetId, currentConversationId, selectConversation]
  );

  // Track visited conversations so their runtimes stay alive (hidden) when
  // switching. Each gets its own AG-UI runtime, HttpAgent, and SSE stream.
  const [visitedIds, setVisitedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!currentConversationId) return;
    setVisitedIds((prev) => {
      if (prev.includes(currentConversationId)) {
        // Move to end (most recently used)
        return [...prev.filter((id) => id !== currentConversationId), currentConversationId];
      }
      const next = [...prev, currentConversationId];
      return next.length > MAX_TABS ? next.slice(-MAX_TABS) : next;
    });
  }, [currentConversationId]);

  const handleDeleteConversation = useCallback(
    (conversationId: string, event: React.MouseEvent) => {
      trackEvent("assistant_conversation_deleted", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      setVisitedIds((prev) => prev.filter((id) => id !== conversationId));
      deleteConversation(conversationId, event);
    },
    [datasetId, deleteConversation]
  );

  const handleRenameConversation = useCallback(
    (conversationId: string, displayName: string) => {
      trackEvent("assistant_conversation_renamed", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      renameConversation(conversationId, displayName);
    },
    [datasetId, renameConversation]
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      trackEvent("assistant_sidebar_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        collapsed: !prev,
      });
      return !prev;
    });
  }, [datasetId]);

  if (isLoadingConversations) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return <AssistantUnavailable className={className} />;
  }

  return (
    <div className={cn("flex h-full bg-white", compactMode ? "flex-col" : "flex-row", className)}>
      {!compactMode && (
        <ConversationSidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          conversationShortcuts={conversationShortcuts}
          isStreaming={isConversationStreaming}
          onNewChat={handleNewChat}
          onSelect={handleConversationSelect}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onToggle={handleToggleSidebar}
          isCollapsed={isSidebarCollapsed}
        />
      )}

      <div className="flex flex-col h-full min-w-0 w-full">
        <div className="flex flex-row items-center justify-between px-4 h-[52px] min-h-[52px] shrink-0 w-full border-b border-black/[0.12]">
          {compactMode ? (
            <div className="flex flex-row gap-0.5 items-center">
              <ConversationSelector
                conversations={conversations}
                currentConversationId={currentConversationId}
                conversationShortcuts={conversationShortcuts}
                isCreatingConversation={isCreating}
                isStreaming={isConversationStreaming}
                onSelect={handleConversationSelect}
                onDelete={handleDeleteConversation}
                onRename={handleRenameConversation}
              />
            </div>
          ) : (
            <p className="text-sm font-medium text-gray-700 truncate">
              {conversations.find((c) => c.id === currentConversationId)?.display_name ?? ""}
            </p>
          )}

          <div className="flex flex-row gap-0.5 items-center justify-end">
            {compactMode && (
              <>
                <Tooltip delayDuration={700}>
                  <TooltipTrigger asChild>
                    <IconButton
                      onClick={handleNewChat}
                      disabled={isCreating}
                      variant="ghost"
                      aria-label="New Analysis"
                    >
                      <Plus className="size-4.5 text-gray-600" />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New Analysis</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={700}>
                  <TooltipTrigger asChild>
                    <IconButton
                      onClick={() => setShareModalOpen(true)}
                      variant="ghost"
                      aria-label="Share Analysis"
                      disabled={!currentConversationId}
                    >
                      <ShareIcon className="size-4.5 text-gray-600" />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Share Analysis</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            {!compactMode && (
              <div className="mr-2">
                <Button
                  onClick={() => setShareModalOpen(true)}
                  disabled={!currentConversationId}
                  variant="outline"
                  size="sm"
                >
                  Share
                </Button>
              </div>
            )}
            {headerActions}
          </div>
        </div>

        <div className="flex-1 min-h-0 relative">
          {visitedIds.map((id) => (
            <div
              key={id}
              className="absolute inset-0"
              data-active={id === currentConversationId ? "" : undefined}
              style={{
                visibility: id === currentConversationId ? "visible" : "hidden",
                pointerEvents: id === currentConversationId ? "auto" : "none",
              }}
            >
              <ConversationRuntime
                conversationId={id}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                api={api}
                suggestions={suggestions}
                updateConversationName={updateConversationName}
                refreshSkills={refreshSkills}
                skillsEnabled={skillsEnabled}
                systemSkills={systemSkills}
                userSkills={userSkills}
                datasetSkills={datasetSkills}
                isLoadingSkills={isLoadingSkills}
                createSkill={createSkill}
                updateSkill={updateSkill}
                deleteSkill={deleteSkill}
                getSkill={getSkill}
                mcpServers={visibleServers}
                selectedMcpServers={selectedMcpServers}
                toggleMcpServer={toggleMcpServer}
              />
            </div>
          ))}
        </div>
      </div>

      {currentConversationId && (
        <ShareConversationModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          conversationId={currentConversationId}
        />
      )}
    </div>
  );
}
